import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import { view, dialogs, i18next, customHtml } from '@yola/ws-sdk';
import {
  Button,
  ButtonGroup,
  Modal,
  Panel,
  PanelGroup,
  Text,
  designSystem,
  reactHookForm,
} from '@yola/ws-ui';
import { ViewportConsumer } from 'yola-editor/src/js/utils/viewport-provider';
import helpCenter from 'yola-editor/src/js/modules/help-center';
import useMatchBreakpoint from 'src/js/modules/utils/custom-hooks/use-match-breakpoint';
import CustomHtmlRenderOption from 'src/js/modules/common/components/custom-html-render-option';
import getCustomHtmlCodeEditorAnnotations from 'src/js/modules/common/helpers/get-custom-html-code-editor-annotations';
import constants from '../constants/common';
import * as keyValues from '../constants/key-values';
import getElementContent from '../helpers/get-element-content';
import updateContent from '../helpers/update-content';
import preventEditorZooming from '../helpers/ios-workarounds/prevent-editor-zooming';

const { renderStatuses, RUNTIME_RENDERING_ATTRIBUTE } = customHtml.constants;
const {
  SimpleSelect,
  Heading,
  Box,
  HorizontalStack,
  NotificationMessage,
  Paragraph,
  CodeEditorField,
} = designSystem;
const { useForm } = reactHookForm;
const MENU_WIDTH = '160px';
const DESKTOP_BREAKPOINT = '992px';
const FIELD_NAME = 'htmlCode';

const EmbedHtmlSettingsDialog = ({ elementId, onDialogMainAction, onDialogCancel }) => {
  const element = view.accessors.getLiveElement(elementId);
  const position = dialogs.helpers.getPositionByElement(
    element,
    constants.WIDGET_WIDTH,
    constants.WIDGET_HEIGHT
  );
  const content = getElementContent(element);
  const isRuntimeRenderStatus = customHtml.verifiers.isRuntimeRender();
  const initialIsRuntimeRendering = element.hasAttribute(RUNTIME_RENDERING_ATTRIBUTE);

  const [isRuntimeRendering, setIsRuntimeRendering] = useState(initialIsRuntimeRendering);
  const [modalOffsetX, setModalOffsetX] = useState(position.offsetX);
  const [modalOffsetY, setModalOffsetY] = useState(position.offsetY);

  const editorRef = useRef(null);

  const { control, setError, clearErrors, getValues } = useForm({
    defaultValues: {
      [FIELD_NAME]: content,
    },
  });

  const isDesktopView = useMatchBreakpoint(DESKTOP_BREAKPOINT);

  const closeDialog = useCallback(() => {
    dialogs.operations.hide();
  }, []);

  const onModalResize = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.resize();
    }
  }, []);

  const onCodeEditorError = useCallback(
    (error) => {
      if (error) {
        setError(FIELD_NAME, { type: 'manual', message: error });
      } else {
        clearErrors(FIELD_NAME);
      }
    },
    [setError, clearErrors]
  );

  const onValidate = useCallback(
    ({ value, session }) =>
      getCustomHtmlCodeEditorAnnotations({
        value,
        session,
      }),
    []
  );

  const onHandleSelectModeChange = useCallback((option) => {
    setIsRuntimeRendering(option.value === renderStatuses.RUNTIME);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!editorRef.current) return;

    const errorAnnotation = editorRef.current
      .getSession()
      .getAnnotations()
      .find((annotation) => annotation.type === 'error');

    if (errorAnnotation) {
      const { text, row, column } = errorAnnotation;
      setError(FIELD_NAME, {
        type: 'manual',
        message: `Line ${row + 1}, Column ${column}: ${text}`,
      });
      return;
    }

    const html = getValues(FIELD_NAME) || '';
    onDialogMainAction({
      renderInEditor: isRuntimeRenderStatus && isRuntimeRendering,
    });

    updateContent(element, html, isRuntimeRendering);
    closeDialog();
  }, [
    isRuntimeRendering,
    isRuntimeRenderStatus,
    element,
    getValues,
    onDialogMainAction,
    setError,
    closeDialog,
  ]);

  const handleCancel = useCallback(() => {
    onDialogCancel({
      renderInEditor: isRuntimeRenderStatus && isRuntimeRendering,
    });
    closeDialog();
  }, [isRuntimeRendering, isRuntimeRenderStatus, onDialogCancel, closeDialog]);

  const onKeyDown = useCallback(
    (event) => {
      const editor = editorRef.current;
      if (!editor) return;

      const isEditorFocused = editor.isFocused();

      switch (event.key) {
        case keyValues.ESC: {
          onDialogCancel();

          if (isEditorFocused) {
            editor.blur();
          } else {
            closeDialog();
          }
          break;
        }

        case keyValues.ENTER: {
          if (!isEditorFocused) {
            handleSubmit();
          }
          break;
        }

        default:
          break;
      }
    },
    [onDialogCancel, handleSubmit, closeDialog]
  );

  const onDragEnd = useCallback((event, { x, y }) => {
    setModalOffsetX(x);
    setModalOffsetY(y);
  }, []);

  const isMobile = bowser.mobile;
  const options = [
    { value: renderStatuses.WEB_SITE_ONLY, label: i18next.t('Website only') },
    { value: renderStatuses.RUNTIME, label: i18next.t('Website & Editor') },
  ];
  const translateCustomCodeTutorialUrl = helpCenter.accessors.getTranslateCustomCodeTutorialUrl();
  const notificationDescription = i18next.t(
    'Code editor supports Jinja2/Nunjucks-style templates. Wrap text in {translationTemplate} to translate it automatically across website languages. Text outside the wrapper stays the same.',
    {
      translationTemplate:
        '<span style="padding: 3px 2px; border-radius: 2px; background-color: rgba(31, 155, 191, 0.25);">{% t &quot;...&quot; %}</span>',
    }
  );

  useEffect(() => {
    if (editorRef.current) {
      preventEditorZooming(editorRef.current);
    }
  }, []);

  return (
    <Modal
      {...position}
      className="ws-html-widget-modal"
      width={constants.WIDGET_WIDTH}
      height={constants.WIDGET_HEIGHT}
      minWidth={constants.WIDGET_MIN_WIDTH}
      minHeight={constants.WIDGET_MIN_HEIGHT}
      resizable={!isMobile}
      draggable={!isMobile}
      fullscreen={isMobile}
      dragHandleSelector=".ws-drag-trigger"
      handleSubmit={handleSubmit}
      handleCancel={handleCancel}
      onResize={onModalResize}
      onKeyDown={onKeyDown}
      onDragEnd={onDragEnd}
    >
      <PanelGroup height="100%">
        <Panel
          className="ws-drag-trigger ws-modal__content"
          height={48}
          align="middle"
          theme="gray-100"
        >
          <Text type="heading-6">{i18next.t('Custom HTML')}</Text>
        </Panel>
        <div className="ws-panel ws-panel--theme-white ws-modal__content ws-html-widget-modal__select-mode">
          <Box>
            <Heading type="heading-6">{i18next.t('HTML code')}</Heading>
          </Box>
          {isDesktopView && (
            <HorizontalStack verticalAlign="center">
              <Heading type="heading-6">{i18next.t('Rendering:')}</Heading>
              <SimpleSelect
                options={options}
                defaultValue={isRuntimeRendering ? options[1] : options[0]}
                transparent
                preventOverflow
                noVerticalSpacing
                noHorizontalSpacing
                menuWidth={MENU_WIDTH}
                menuAlignment="right"
                components={{ Option: CustomHtmlRenderOption }}
                onChange={onHandleSelectModeChange}
              />
            </HorizontalStack>
          )}
        </div>
        <div className="ws-panel ws-panel--theme-white ws-modal__content ws-html-widget-modal__editor">
          <CodeEditorField
            className="ws-html-widget-editor"
            name={FIELD_NAME}
            control={control}
            height="stretch"
            options={{
              tabSize: 2,
            }}
            onEditorError={onCodeEditorError}
            onValidate={onValidate}
            onAfterEditorConfigured={(editor) => {
              editorRef.current = editor;
              preventEditorZooming(editor);
            }}
          />
          <Box marginTop="spacing-3-xs">
            <NotificationMessage
              appearance="info"
              iconGlyph="info"
              title={
                <Paragraph weight="medium">{i18next.t('Smart templates supported')}</Paragraph>
              }
              description={
                <Paragraph appearance="medium-emphasis" size="small">
                  {/* eslint-disable-next-line yola/react/no-danger */}
                  <span dangerouslySetInnerHTML={{ __html: notificationDescription }} />{' '}
                  <a
                    href={translateCustomCodeTutorialUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {i18next.t('Learn more')}
                  </a>
                </Paragraph>
              }
            />
          </Box>
        </div>

        <Panel height="48">
          <ButtonGroup stick="top" block>
            <Button stretch="block" size="large" onClick={handleSubmit}>
              <Text type="heading-6">{i18next.t('Submit')}</Text>
            </Button>
            <Button stretch="block" size="large" onMouseDown={handleCancel}>
              <Text type="heading-6">{i18next.t('Cancel')}</Text>
            </Button>
          </ButtonGroup>
        </Panel>
      </PanelGroup>

      <ViewportConsumer>
        {/* eslint-disable yola/react/no-danger */}
        {({ width }) => (
          <style
            dangerouslySetInnerHTML={{
              __html: `
                .ws-html-widget-editor .ace_tooltip {
                  max-width: ${width}px;
                  transform: translate(${-modalOffsetX}px, ${-modalOffsetY}px);
                }
              `,
            }}
          />
        )}
        {/* eslint-enable yola/react/no-danger */}
      </ViewportConsumer>
    </Modal>
  );
};

EmbedHtmlSettingsDialog.propTypes = {
  elementId: PropTypes.string.isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default EmbedHtmlSettingsDialog;
